import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapPin, Calendar, Clock, ArrowRight, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';
import DefaultSEO from '../components/DefaultSEO';

const BookingHistoryPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [bookings, setBookings] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        const fetchBookings = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8001/api/v1';
                const cleanApiBase = apiBase.replace(/\/+$/, '');
                const token = localStorage.getItem('auth_token');

                if (!token) {
                    setError('Please login to view your bookings.');
                    setLoading(false);
                    return;
                }

                const response = await fetch(`${cleanApiBase}/bookings`, {
                    headers: {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                const data = await response.json();
                if (data.success) {
                    setBookings(data.data);
                } else {
                    setError(data.message || 'Failed to load bookings.');
                }
            } catch (err) {
                console.error("Fetch bookings error:", err);
                setError('An error occurred while loading your bookings.');
            } finally {
                setLoading(false);
            }
        };

        fetchBookings();
    }, []);

    // Helper to resolve the correct property image URL
    const getPropertyImage = (property) => {
        if (property?.images?.[0]?.file_path) {
            const path = property.images[0].file_path;
            if (path.startsWith('http')) {
                return path;
            }
            const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
            try {
                const url = new URL(apiBase);
                return `${url.protocol}//${url.host}/storage/${path}`;
            } catch {
                return `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${path}`;
            }
        }
        return 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80';
    };

    // Helper to format dates safely
    const formatDateSafe = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'dd MMM yyyy');
        } catch {
            return dateString;
        }
    };

    // Helper to calculate total nights of the booking
    const calculateNights = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return 1;
        try {
            const diffTime = Math.abs(new Date(checkOut) - new Date(checkIn));
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays || 1;
        } catch {
            return 1;
        }
    };

    const getStatusStyles = (status) => {
        const cleanStatus = (status || 'pending').toLowerCase();
        switch (cleanStatus) {
            case 'confirmed':
                return {
                    badge: 'bg-success bg-opacity-10 text-success border-success',
                    indicator: '#198754'
                };
            case 'completed':
                return {
                    badge: 'bg-primary bg-opacity-10 text-primary border-primary',
                    indicator: '#0d6efd'
                };
            case 'cancelled':
                return {
                    badge: 'bg-danger bg-opacity-10 text-danger border-danger',
                    indicator: '#dc3545'
                };
            default:
                return {
                    badge: 'bg-warning bg-opacity-10 text-warning-emphasis border-warning',
                    indicator: '#ffc107'
                };
        }
    };

    const queryParams = new URLSearchParams(location.search);
    const filterParam = queryParams.get('filter') || 'all';
    const activeTab = ['all', 'active', 'completed', 'cancelled'].includes(filterParam) ? filterParam : 'all';

    const filteredBookings = bookings.filter((booking) => {
        const status = (booking.status || 'pending').toLowerCase();
        const today = new Date().toISOString().split('T')[0];
        
        if (activeTab === 'active') {
            return status === 'confirmed' && booking.check_out_date >= today;
        }
        if (activeTab === 'completed') {
            return status === 'completed' || (status === 'confirmed' && booking.check_out_date < today);
        }
        if (activeTab === 'cancelled') {
            return status === 'cancelled';
        }
        return true; // 'all'
    });

    return (
        <React.Fragment>
            <DefaultSEO title="My Bookings | ZoyoTrip" />
            <div className="container pt-3 pt-md-5 pb-5 min-vh-100">
                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight mb-1">Your Bookings</h1>
                        <p className="text-gray-500 font-medium text-sm md:text-base mb-0">View and manage your upcoming stays and past trips.</p>
                    </div>
                </div>

                {/* Tab controls */}
                <div className="d-flex flex-nowrap gap-2 overflow-x-auto pb-3 mb-4 border-bottom scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    {['all', 'active', 'completed', 'cancelled'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => {
                                navigate(tab === 'all' ? '/account/bookings' : `/account/bookings?filter=${tab}`, { replace: true });
                            }}
                            className={`btn btn-sm rounded-pill px-3 py-2 fw-semibold text-capitalize transition flex-shrink-0 ${
                                activeTab === tab 
                                    ? 'btn-primary shadow-sm' 
                                    : 'btn-outline-secondary border-0 text-muted hover:bg-light'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="d-flex justify-content-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger rounded-4 shadow-sm border-0 py-3">{error}</div>
                ) : bookings.length === 0 ? (
                    <div className="text-center py-5 bg-white rounded-4 border p-5 shadow-sm">
                        <ShieldCheck size={60} className="text-muted mb-3 mx-auto d-block" />
                        <h4 className="fw-bold text-dark mb-2">No Bookings Found</h4>
                        <p className="text-muted mb-4">You haven't booked any hotels yet. Explore our top properties now.</p>
                        <button onClick={() => navigate('/')} className="btn btn-primary rounded-pill px-5 py-3 fw-bold">
                            Explore Hotels
                        </button>
                    </div>
                ) : filteredBookings.length === 0 ? (
                    <div className="text-center py-5 bg-white rounded-4 border p-5 shadow-sm">
                        <ShieldCheck size={60} className="text-muted mb-3 mx-auto d-block" />
                        <h4 className="fw-bold text-dark mb-2">No {activeTab !== 'all' ? activeTab : ''} Bookings Found</h4>
                        <p className="text-muted mb-4">You don't have any {activeTab !== 'all' ? activeTab : ''} bookings at the moment.</p>
                        {activeTab === 'all' && (
                            <button onClick={() => navigate('/')} className="btn btn-primary rounded-pill px-5 py-3 fw-bold">
                                Explore Hotels
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="row g-4">
                        {filteredBookings.map((booking) => {
                            const statusStyle = getStatusStyles(booking.status);
                            const totalNights = calculateNights(booking.check_in_date, booking.check_out_date);

                            return (
                                <div key={booking.id} className="col-12">
                                    <div 
                                        onClick={() => navigate(`/account/bookings/${booking.id}`)}
                                        className="card booking-card shadow-sm border-0 cursor-pointer position-relative mb-2"
                                        title="Click to view booking details"
                                    >
                                        {/* Status vertical left border indicator */}
                                        <div 
                                            className="status-bar" 
                                            style={{ backgroundColor: statusStyle.indicator }}
                                        />

                                        <div className="row g-0 align-items-stretch">
                                            {/* Hotel Image (Desktop only, lg and up) */}
                                            <div className="col-lg-3 d-none d-lg-block img-zoom-container position-relative">
                                                <img
                                                    src={getPropertyImage(booking.property)}
                                                    alt={booking.property?.name || 'Hotel Image'}
                                                    className="img-fluid h-100 w-100 object-fit-cover position-absolute top-0 start-0"
                                                />
                                            </div>

                                            {/* Stay Info & Price */}
                                            <div className="col-lg-9 col-12">
                                                <div className="card-body p-3 p-md-4 d-flex flex-column h-100 justify-content-between">
                                                    
                                                    {/* Mobile Header (displayed on screens below lg) */}
                                                    <div className="d-flex d-lg-none align-items-start gap-3 mb-2">
                                                        <div className="position-relative" style={{ width: '80px', height: '80px', flexShrink: 0, overflow: 'hidden', borderRadius: '12px' }}>
                                                            <img
                                                                src={getPropertyImage(booking.property)}
                                                                alt={booking.property?.name || 'Hotel Image'}
                                                                className="w-100 h-100 object-fit-cover"
                                                            />
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <div className="d-flex justify-content-between align-items-start gap-1">
                                                                <h5 className="fw-bold text-dark mb-1 fs-6 lh-sm hotel-title-hover transition">
                                                                    {booking.property?.name || 'Star County'}
                                                                </h5>
                                                                <span className={`badge border ${statusStyle.badge} px-2 py-1 rounded-pill fw-bold text-capitalize`} style={{ fontSize: '10px' }}>
                                                                    {booking.status}
                                                                </span>
                                                            </div>
                                                            <span className="text-muted d-block small mb-1 d-flex align-items-center" style={{ fontSize: '12px' }}>
                                                                <MapPin size={12} className="me-1 text-primary" style={{ flexShrink: 0 }} /> 
                                                                <span className="text-truncate" style={{ maxWidth: '160px' }}>
                                                                    {booking.property?.address ? `${booking.property.address}, ${booking.property.city || ''}`.replace(/,\s*$/, '') : (booking.property?.location || booking.property?.city || 'Unknown Location')}
                                                                </span>
                                                            </span>
                                                            <span className="text-secondary font-monospace bg-light px-2 py-0.5 rounded" style={{ fontSize: '11px' }}>
                                                                ID: {booking.booking_id}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Desktop Header portion (displayed on screens lg and up) */}
                                                    <div className="d-none d-lg-flex justify-content-between align-items-start gap-2 mb-3">
                                                        <div>
                                                            <h4 className="fw-bold text-dark mb-1 hotel-title-hover transition">
                                                                {booking.property?.name || 'Star County'}
                                                            </h4>
                                                            <div className="d-flex flex-wrap gap-3 align-items-center">
                                                                <span className="text-muted small d-flex align-items-center">
                                                                    <MapPin size={15} className="me-1 text-primary" /> 
                                                                    {booking.property?.address ? `${booking.property.address}, ${booking.property.city || ''}`.replace(/,\s*$/, '') : (booking.property?.location || booking.property?.city || 'Unknown Location')}
                                                                </span>
                                                                <span className="text-secondary font-monospace small bg-light px-2 py-1 rounded">
                                                                    ID: {booking.booking_id}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <span className={`badge border ${statusStyle.badge} px-3 py-2 rounded-pill fw-bold text-capitalize`}>
                                                            {booking.status}
                                                        </span>
                                                    </div>

                                                    <hr className="d-none d-lg-block my-3 text-muted opacity-25" />

                                                    {/* Content block with responsive children */}
                                                    <div className="d-flex flex-column gap-2">
                                                        
                                                        {/* Desktop Layout for Dates and Price */}
                                                        <div className="d-none d-lg-flex flex-wrap justify-content-between align-items-end gap-3">
                                                            <div className="d-flex flex-wrap gap-4 align-items-center">
                                                                <div className="bg-light p-3 rounded-3 d-flex align-items-center gap-3">
                                                                    <Calendar size={22} className="text-primary" />
                                                                    <div>
                                                                        <small className="text-muted d-block small mb-0.5">Check-in</small>
                                                                        <strong className="text-dark">{formatDateSafe(booking.check_in_date)}</strong>
                                                                    </div>
                                                                </div>

                                                                <div className="text-muted px-2">
                                                                    <ArrowRight size={18} />
                                                                </div>

                                                                <div className="bg-light p-3 rounded-3 d-flex align-items-center gap-3">
                                                                    <Calendar size={22} className="text-primary" />
                                                                    <div>
                                                                        <small className="text-muted d-block small mb-0.5">Check-out</small>
                                                                        <strong className="text-dark">{formatDateSafe(booking.check_out_date)}</strong>
                                                                    </div>
                                                                </div>

                                                                <div className="badge bg-secondary bg-opacity-10 text-secondary border px-3 py-2 rounded-pill d-flex align-items-center gap-1">
                                                                    <Clock size={14} />
                                                                    <span>{totalNights} {totalNights === 1 ? 'Night' : 'Nights'} Stay</span>
                                                                </div>
                                                            </div>

                                                            <div className="text-end">
                                                                <small className="text-muted d-block mb-1">Total Paid</small>
                                                                <h3 className="fw-bold text-dark mb-1">
                                                                    ₹{parseFloat(booking.total_price || 0).toLocaleString('en-IN')}
                                                                </h3>
                                                                <div className="d-flex align-items-center justify-content-end gap-3 mt-2">
                                                                    <span className="text-primary fw-semibold small d-inline-flex align-items-center gap-1 view-details-link">
                                                                        View Details <ArrowRight size={14} className="transition-arrow" />
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Mobile/Tablet Layout (displayed on screens below lg) */}
                                                        <div className="d-block d-lg-none">
                                                            {/* Mobile Dates Pill Row */}
                                                            <div className="d-flex align-items-center justify-content-between bg-light p-2 rounded-3 my-2 text-dark small gap-1">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Calendar size={14} className="text-primary" />
                                                                    <div className="lh-sm">
                                                                        <span className="text-muted d-block" style={{ fontSize: '9px' }}>Check-in</span>
                                                                        <strong style={{ fontSize: '11px' }}>{formatDateSafe(booking.check_in_date)}</strong>
                                                                    </div>
                                                                </div>
                                                                <ArrowRight size={12} className="text-muted mx-1" />
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Calendar size={14} className="text-primary" />
                                                                    <div className="lh-sm">
                                                                        <span className="text-muted d-block" style={{ fontSize: '9px' }}>Check-out</span>
                                                                        <strong style={{ fontSize: '11px' }}>{formatDateSafe(booking.check_out_date)}</strong>
                                                                    </div>
                                                                </div>
                                                                <div className="badge bg-secondary bg-opacity-10 text-secondary border px-2 py-1 rounded-pill d-flex align-items-center gap-0.5 ms-auto" style={{ fontSize: '10px' }}>
                                                                    <Clock size={10} />
                                                                    <span>{totalNights} N</span>
                                                                </div>
                                                            </div>

                                                            {/* Mobile Price & Buttons Row */}
                                                            <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                                                                <div>
                                                                    <small className="text-muted d-block" style={{ fontSize: '10px', marginBottom: '-2px' }}>Total Paid</small>
                                                                    <span className="fw-bold text-dark fs-5">
                                                                        ₹{parseFloat(booking.total_price || 0).toLocaleString('en-IN')}
                                                                    </span>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span className="text-primary fw-semibold small d-inline-flex align-items-center gap-0.5 view-details-link" style={{ fontSize: '12px' }}>
                                                                        Details <ArrowRight size={12} className="transition-arrow" />
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <style>{`
                .booking-card {
                    border: 1px solid rgba(0, 0, 0, 0.08) !important;
                    border-radius: 16px !important;
                    overflow: hidden;
                    transition: transform 0.25s ease, box-shadow 0.25s ease;
                }
                .booking-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08) !important;
                }
                .status-bar {
                    width: 6px;
                    height: 100%;
                    position: absolute;
                    left: 0;
                    top: 0;
                    z-index: 10;
                }
                .img-zoom-container {
                    overflow: hidden;
                    min-height: 180px;
                }
                .img-zoom-container img {
                    transition: transform 0.3s ease;
                }
                .booking-card:hover .img-zoom-container img {
                    transform: scale(1.05);
                }
                .hotel-title-hover {
                    color: #212529;
                }
                .booking-card:hover .hotel-title-hover {
                    color: #0d6efd;
                }
                .transition {
                    transition: all 0.25s ease-in-out;
                }
                .transition-arrow {
                    transition: transform 0.2s ease;
                }
                .booking-card:hover .transition-arrow {
                    transform: translateX(4px);
                }
            `}</style>
        </React.Fragment>
    );
};

export default BookingHistoryPage;
